import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  CreateOrUpdateRemarkModel,
  GetRemarksParams,
  GetRemarksResponse,
  ProblemDetails
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Returns a list of remarks<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetRemarksHook = () => {
        const getRemarks = useCustomWelcomeClient<GetRemarksResponse>();

        return useCallback((
    id: string,
    params?: GetRemarksParams,
 signal?: AbortSignal
) => {


        return getRemarks(
          {url: `/requests/${id}/remarks`, method: 'GET',
        params, signal
    },
          );
        }, [getRemarks])
      }




export const getGetRemarksQueryKey = (id: string,
    params?: GetRemarksParams,) => {
    return [
    `/requests/${id}/remarks`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetRemarksQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    params?: GetRemarksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRemarksQueryKey(id,params);

  const getRemarks =  useGetRemarksHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>> = ({ signal }) => getRemarks(id,params, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetRemarksQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>>
export type GetRemarksQueryError = ErrorType<ProblemDetails>


export function useGetRemarks<TData = Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params: undefined |  GetRemarksParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRemarks<TData = Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params?: GetRemarksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRemarks<TData = Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params?: GetRemarksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetRemarks<TData = Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params?: GetRemarksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRemarksHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetRemarksQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Adds a remark
 */
export const useCreateRemarkHook = () => {
        const createRemark = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    createOrUpdateRemarkModel: CreateOrUpdateRemarkModel,
 signal?: AbortSignal
) => {


        return createRemark(
          {url: `/requests/${id}/remarks`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createOrUpdateRemarkModel, signal
    },
          );
        }, [createRemark])
      }




export const useCreateRemarkMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRemarkHook>>>, TError,CreateRemarkMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRemarkHook>>>, TError,CreateRemarkMutationVariables, TContext> => {

const mutationKey = ['createRemark'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createRemark =  useCreateRemarkHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateRemarkHook>>>, CreateRemarkMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createRemark(id,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateRemarkMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateRemarkHook>>>>
    export type CreateRemarkMutationBody = CreateOrUpdateRemarkModel
    export type CreateRemarkMutationError = ErrorType<ProblemDetails>
    export type CreateRemarkMutationVariables = {id: string;data: CreateOrUpdateRemarkModel}

    export const useCreateRemark = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRemarkHook>>>, TError,CreateRemarkMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateRemarkHook>>>,
        TError,
        CreateRemarkMutationVariables,
        TContext
      > => {
      return useMutation(useCreateRemarkMutationOptions(options), queryClient);
    }
